import { CompactField } from 'components/field';
import style from './tract-details.module.scss';
import { WorkflowStatusPopover } from './workflow-status-popover';
import { Button } from '@carbon/react';
import { Edit } from '@carbon/react/icons';
import { useAtomValue } from 'jotai';
import { useWizardStep } from 'components/full-page-wizard';
import { formatDecimalFull } from 'lib/ui';
import { isNil } from 'lodash-es';
import { formatNumber } from 'utils/number';
import {
  crmCreatePermissionLoadableAtom,
  createdSubtractDetailsLoadableAtom,
  createdTractDetailsLoadableAtom,
  workflowStatusAtom,
} from './atoms';
import { SkeletonTextRows } from 'components/skeleton-text-rows/skeleton-text-rows';
import { formatTitleRiskValue } from './utils';

interface TractDetailsProps {
  isWorkflowStatusVisible?: boolean;
}
export const TractDetails = ({
  isWorkflowStatusVisible = false,
}: TractDetailsProps) => {
  const createdTractDetailsLoadable = useAtomValue(
    createdTractDetailsLoadableAtom
  );
  const createdSubtractDetailsLoadable = useAtomValue(
    createdSubtractDetailsLoadableAtom
  );
  const workflowStatus = useAtomValue(workflowStatusAtom);
  const { goToStep } = useWizardStep();
  const crmCreatePermission = useAtomValue(crmCreatePermissionLoadableAtom);
  const hasCrmCreatePermission =
    crmCreatePermission.state === 'hasData' &&
    crmCreatePermission.data?.hasPermissions;

  const isLoading =
    createdTractDetailsLoadable.state === 'loading' ||
    createdSubtractDetailsLoadable.state === 'loading';

  const tract =
    createdTractDetailsLoadable.state === 'hasData'
      ? createdTractDetailsLoadable.data
      : null;
  const subtract =
    createdSubtractDetailsLoadable.state === 'hasData'
      ? createdSubtractDetailsLoadable.data
      : null;

  if (isLoading) {
    return <SkeletonTextRows rows={8} />;
  }

  return (
    <div>
      <div className={style.tractDetailsContainer}>
        <div className={style.tractDetailsSection}>
          <div className={style.tractDetailsHeader}>
            <span>Tract Details</span>
            {isWorkflowStatusVisible && (
              <WorkflowStatusPopover
                statusItems={workflowStatus.statusItems}
                overallStatus={workflowStatus.overallStatus}
              />
            )}
            <Button
              kind="primary"
              size="sm"
              renderIcon={Edit}
              onClick={() => goToStep(2)}
              disabled={!hasCrmCreatePermission}
            >
              Edit Tract
            </Button>
          </div>
          <div className={style.tractDetailsContent}>
            <div>
              <CompactField label="Tract Name:" value={tract?.name} />
              <CompactField
                label="Survey Area:"
                value={tract?.referenceLocation?.name}
              />
              <CompactField label="Quarter Call:" value={tract?.quarterCall} />
              <CompactField label="Lots:" value={tract?.lots} />
              <CompactField
                label="Grid Exception:"
                value={tract?.gridException?.toString()}
              />
              <CompactField label="Notes:" value={tract?.notes} />
              <CompactField
                label="Title Quality:"
                value={formatTitleRiskValue(
                  tract?.subtracts?.[0]?.titleQuality
                )}
              />
              <CompactField
                label="Title Risk Factor:"
                value={formatTitleRiskValue(
                  tract?.subtracts?.[0]?.titleRiskFactor
                )}
              />
            </div>
            <div>
              <CompactField label="Instrument Type:" value={tract?.assetType} />
              <CompactField label="Tract Source:" value={tract?.tractSource} />
              <CompactField
                label="Start Depth:"
                value={
                  !isNil(tract?.startDepth)
                    ? formatDecimalFull(tract.startDepth)
                    : undefined
                }
              />
              <CompactField
                label="End Depth:"
                value={
                  !isNil(tract?.endDepth)
                    ? formatDecimalFull(tract.endDepth)
                    : undefined
                }
              />
              <CompactField label="Alt Tract ID:" value={tract?.altAssetId} />
              <CompactField
                label="Net Acreage Units:"
                value={tract?.netAcreageUnits}
              />
              <CompactField
                label="Tract Net Acres:"
                value={formatNumber({
                  value: tract?.tractNetAcres,
                  minPrecision: 0,
                  maxPrecision: 8,
                  includeCommas: true,
                })}
              />
              <CompactField
                label="Tract Gross Acres:"
                value={formatNumber({
                  value: tract?.grossAcres,
                  minPrecision: 0,
                  maxPrecision: 8,
                  includeCommas: true,
                })}
              />
            </div>
          </div>
        </div>

        <div className={style.subtractDetailsSection}>
          <div className={style.subtractDetailsHeader}>
            <span>Subtract Details</span>
          </div>

          <div className={style.subtractDetailsContent}>
            <div>
              <CompactField label="Subtract Name:" value={subtract?.name} />
              <CompactField
                label="Survey Area:"
                value={tract?.referenceLocation?.name}
              />
              <CompactField
                label="Quarter Call:"
                value={subtract?.legalDescription?.quarterCall}
              />
              <CompactField
                label="Full Legal Description:"
                value={subtract?.legalDescription?.fullLegalDescription}
              />
              <CompactField
                label="Lots:"
                value={subtract?.legalDescription?.lot}
              />
              <CompactField
                label="Grid Exception:"
                value={subtract?.legalDescription?.gridException?.toString()}
              />
              <CompactField
                label="Subtract Net Acres:"
                value={formatNumber({
                  value: subtract?.acres,
                  minPrecision: 0,
                  maxPrecision: 8,
                  includeCommas: true,
                })}
              />
              <CompactField
                label="Subtract Gross Acres:"
                value={formatNumber({
                  value: subtract?.grossAcres,
                  minPrecision: 0,
                  maxPrecision: 8,
                  includeCommas: true,
                })}
              />
            </div>
            <div></div>
          </div>
        </div>
      </div>
    </div>
  );
};
